'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';
import {
  Sword, Crosshair, Shield, Gauge, Trophy, Flame,
  Cpu, Brain, HelpCircle, Gamepad2, Heart, Music,
} from 'lucide-react';
import { useT } from '@/hooks/useT';
import { usePriceConvert } from '@/lib/PriceContext';
import type { I18nKey } from '@/lib/i18n';
import { stagger, staggerItem, fadeUp, viewportOnce } from '@/v4/theme/motion';

const GENRES: { key: string; labelKey: I18nKey; Icon: typeof Sword; color: string }[] = [
  { key: 'Action & adventure', labelKey: 'genre_action',     Icon: Sword,      color: '#ef4444' },
  { key: 'Shooter',            labelKey: 'genre_shooter',    Icon: Crosshair,  color: '#f97316' },
  { key: 'Role playing',       labelKey: 'genre_rpg',        Icon: Shield,     color: '#a78bfa' },
  { key: 'Racing & flying',    labelKey: 'genre_racing',     Icon: Gauge,      color: '#06b6d4' },
  { key: 'Sports',             labelKey: 'genre_sports',     Icon: Trophy,     color: '#fbbf24' },
  { key: 'Fighting',           labelKey: 'genre_fighting',   Icon: Flame,      color: '#fb923c' },
  { key: 'Simulation',         labelKey: 'genre_simulation', Icon: Cpu,        color: '#34d399' },
  { key: 'Strategy',           labelKey: 'genre_strategy',   Icon: Brain,      color: '#60a5fa' },
  { key: 'Puzzle & trivia',    labelKey: 'genre_puzzle',     Icon: HelpCircle, color: '#f472b6' },
  { key: 'Platformer',         labelKey: 'genre_platformer', Icon: Gamepad2,   color: '#4ade80' },
  { key: 'Family & kids',      labelKey: 'genre_family',     Icon: Heart,      color: '#fb7185' },
  { key: 'Music',              labelKey: 'genre_music',      Icon: Music,      color: '#c084fc' },
];

const HEADING: Record<string, string> = { uk: 'Категорії', ru: 'Категории', en: 'Categories' };

export function GenreGrid() {
  const t = useT();
  const { lang } = usePriceConvert();

  return (
    <motion.section {...fadeUp()}>
      <div className="v4home-sec-hd">
        <h2 className="v4home-sec-title">{HEADING[lang] ?? HEADING.en}</h2>
        <Link href="/catalog" className="v4home-sec-link">
          {t('footer_all_games')} →
        </Link>
      </div>
      <motion.div
        className="v4home-genre-grid"
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={viewportOnce}
      >
        {GENRES.map(({ key, labelKey, Icon, color }) => (
          <motion.div key={key} variants={staggerItem}>
            <Link
              href={`/catalog?genre=${encodeURIComponent(key)}`}
              className="v4home-genre-tile"
              style={{ '--g-color': color } as React.CSSProperties}
            >
              <div className="v4home-genre-icon">
                <Icon size={22} strokeWidth={1.8} />
              </div>
              <span className="v4home-genre-label">{t(labelKey)}</span>
            </Link>
          </motion.div>
        ))}
      </motion.div>
    </motion.section>
  );
}
